import { Skeleton } from '@/shared/components/ui/Skeleton';
import { getApiErrorMessage, getApiErrorStatus } from '@/shared/api/axios';

// The three non-happy states every read-only accounts report must render.
//
// A report that shows an empty table when the server answered 403, or a table
// of ₹0 when the ledger account does not exist yet, teaches the wrong lesson:
// the reader concludes there is no money moving. These panels keep "nothing
// happened", "this could not be read", and "you are not allowed to read it" as
// three visibly different answers.

export function ReportLoading({ rows = 3 }: { rows?: number }) {
  return (
    <div className="space-y-2 p-4">
      {Array.from({ length: rows }, (_, i) => <Skeleton key={i} className="h-10 w-full" />)}
    </div>
  );
}

export function ReportEmpty({ text }: { text: string }) {
  return <p className="py-12 text-center text-sm text-muted-foreground">{text}</p>;
}

/** 403 gets its own wording — only one of these two problems is fixed by posting vouchers. */
export function ReportProblem({ error, subject }: { error: unknown; subject: string }) {
  const status = getApiErrorStatus(error);
  if (status === 403) {
    return (
      <div className="m-4 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-200">
        <p className="font-semibold">Permission denied</p>
        <p className="mt-1 text-body-sm">
          The server refused this request (HTTP 403). {subject} is limited to the Accounts
          department or an administrator. No figures are shown.
        </p>
      </div>
    );
  }
  return (
    <div className="m-4 rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800 dark:border-rose-900/60 dark:bg-rose-950/30 dark:text-rose-200">
      {getApiErrorMessage(error, `Could not load ${subject}.`)}
    </div>
  );
}
